import React, { useState } from 'react';
import { cloneDeep } from 'lodash';
import Select from 'react-select';
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useGradingGroupState } from './GradingGroupProvider';
import { SystemMasterSelect, checkArray, showBigImage, hideImage, shortenandHoverString } from '../../../../Components/helper';
import noImage from '../../../../assets/images/no-image.png';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import ConfirmationPopup from '../../../../Components/ModalPopup/ConfirmationPopup';
import GradingCell from './GradingCell';
import { pomRowKey } from './pomRowIdentity';

const defaultSizeRangeColum = [
  {
    sizeName: '',
    isSampleSize: true,
    isZero: true,
    grading: 0,
  },
  {
    sizeName: '',
    isSampleSize: false,
    isZero: false,
    grading: 0,
  },
  {
    sizeName: '',
    isSampleSize: false,
    isZero: false,
    grading: 0,
  },
];

function GradingTable() {
  const {
    gradingGroupState,
    setgradingGroup,
    handleCheckPOMList,
    handleCheckAll,
    isallChecked,
    ecommAcess,
    eCommSpecData,
    GradingDeleteAccess,
    GradingAddEditAccess,
    removePomsFromGroup,
  } = useGradingGroupState();
  const { t } = useTranslation('common');

  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  /**
   * Opens the confirm step. The delete is persisted immediately on confirm, so
   * it is not undoable by simply leaving the page without saving.
   */
  const handleSingleDelete = (id) => {
    setPendingDeleteId(id);
  };

  const closeDeleteConfirm = () => {
    setPendingDeleteId(null);
  };

  const confirmSingleDelete = async () => {
    const targetId = pendingDeleteId;
    setPendingDeleteId(null);
    await removePomsFromGroup([targetId]);
  };

  const handleData = (pomIndex, e, index) => {
    // Create a deep copy of the pomTableData to avoid reference issues
    const clonePomTable = JSON.parse(JSON.stringify(gradingGroupState?.pomTableData));
    if (clonePomTable[pomIndex].gradings[index]?.grading !== undefined) {
      clonePomTable[pomIndex].gradings[index].grading = e;
    }

    setgradingGroup((prev) => ({
      ...prev,
      pomTableData: clonePomTable,
    }));
  };

  const checkMeasurementUnitExist = (unitId, index, clonePomTable) => {
    const POMData = clonePomTable;
    // eslint-disable-next-line no-unused-expressions
    POMData?.length > 0 &&
      POMData?.forEach((eleSpec, i) => {
        if (eleSpec.ecommSpec?._id === unitId) {
          if (index !== i) {
            POMData[i].ecommSpec = null;
          }
          setgradingGroup((prev) => ({
            ...prev,
            pomTableData: POMData,
          }));
        }
      });
  };

  const handleEcommOptionSelect = (pomIndex, selected) => {
    const clonePomTable = cloneDeep(gradingGroupState?.pomTableData);
    clonePomTable[pomIndex].ecommSpec = selected;
    setgradingGroup((prev) => ({
      ...prev,
      pomTableData: clonePomTable,
    }));
    checkMeasurementUnitExist(selected._id, pomIndex, clonePomTable);
  };

  const handleToleranceData = (pomIndex, e) => {
    const clonePomTable = cloneDeep(gradingGroupState?.pomTableData);
    const regex = /^\d*\.?\d{0,3}$/;
    if (regex.test(e)) {
      clonePomTable[pomIndex].tolMinus = e;
      clonePomTable[pomIndex].tolPlus = e;
    }
    setgradingGroup((prev) => ({
      ...prev,
      pomTableData: clonePomTable,
    }));
  };

  const forImage = (item) => {
    if (item?.annotatedImage) {
      return item.annotatedImage;
    }
    if (!item?.image || item.image.includes(' ')) {
      return noImage;
    }
    if (item.image.includes('data:image')) {
      return item.image;
    }
    return item.image;
  };

  return (
    <Container className="grading-group-table" fluid>
      <div className="grading-pom-scroll pom-sroll-container">
        <table className="table common-custom-table fixed-header layout-auto mb-0">
          <thead>
            <tr>
              <th className="w-30px">
                <div className="common-checkbox">
                  <input
                    type="checkbox"
                    name="check_all_symbol"
                    id="check_all_pom"
                    onChange={(e) => handleCheckAll(e)}
                    checked={isallChecked}
                    disabled={!GradingAddEditAccess}
                  />
                  <label htmlFor="check_all_pom" className="chklabel">
                    <span />
                  </label>
                </div>
              </th>

              <th className="w-100px">{t('POMS.GRADING_GROUP.GRADING_TABLE.IMAGE')}</th>
              <th className="w-224px">{t('POMS.GRADING_GROUP.GRADING_TABLE.POM_CODE')}</th>
              <th className="w-350px"> {t('POMS.GRADING_GROUP.GRADING_TABLE.POM_DESCRIPTION')}</th>
              <th className="pt-0 pb-0">
                <table className="inner-size-table">
                  <thead>
                    <tr>
                      <th className="p-0 size-head border-none" colSpan="100%">
                        {t('POMS.GRADING_GROUP.GRADING_TABLE.SIZE_RANGE_GRADING')}
                      </th>
                    </tr>
                    {gradingGroupState?.applySizeRangesToHeaderText && (
                      <tr>
                        {gradingGroupState?.sizeRangesForHeader?.map((data) => {
                          return (
                            <th className="pl-0 size-head border-none pt-0 pb-0 pr-0" key={data.sizeName ? data.sizeName : data.name}>
                              {data.sizeName ? data.sizeName : data.name}
                            </th>
                          );
                        })}
                      </tr>
                    )}
                  </thead>
                </table>
              </th>

              <th className="w-224px">{t('POMS.GRADING_GROUP.GRADING_TABLE.TOLERANCE')} </th>
              {ecommAcess?.[0]?.isActive && <th className="w-224px">{t('POMS.GRADING_GROUP.GRADING_TABLE.ECOMM_SPEC')} </th>}
              {GradingDeleteAccess && <th className="w-50px text-right" />}
            </tr>
          </thead>
          <tbody>
            {checkArray(gradingGroupState?.pomTableData) ? (
              gradingGroupState?.pomTableData.map((eleItem, i) => {
                /*
                 * Stored POM rows are not guaranteed to carry an `_id`, and keying selection off
                 * it gave every such row the same `undefined` identity — ticking one ticked all.
                 */
                const rowKey = pomRowKey(eleItem);
                return (
                  <tr key={rowKey ?? `pom-row-${i}`}>
                    <td>
                      <div className="common-checkbox">
                        <input
                          type="checkbox"
                          name={`pom_${i}`}
                          id={`pom_${i}`}
                          checked={Boolean(rowKey) && gradingGroupState?.selectedPOM?.includes(rowKey)}
                          onChange={(e) => handleCheckPOMList(e.target.checked, rowKey)}
                          disabled={!GradingAddEditAccess || !rowKey}
                        />
                        <label htmlFor={`pom_${i}`} className="chklabel">
                          <span />
                        </label>
                      </div>
                    </td>
                    <td>
                      <div className="image-detail hover-right">
                        <figure onMouseOver={(e) => showBigImage(e)} onMouseLeave={() => hideImage()} className="m-0">
                          <div className="">
                            <img width="48" alt="product img" src={forImage(eleItem)} />
                          </div>
                        </figure>
                      </div>
                    </td>

                    <td>{shortenandHoverString(eleItem?.code, 20)}</td>
                    <td>{shortenandHoverString(eleItem?.description, 40)}</td>
                    <td className="pt-0 pb-0">
                      {gradingGroupState?.applySizeRangesToHeaderText ? (
                        <table className="inner-size-table">
                          <tr>
                            {gradingGroupState?.sizeRangesForHeader?.map((data, index) => {
                              return (
                                <th className="pl-0 text-center pt-0 pb-0 pr-16" key={data.sizeName ? data.sizeName : data.name}>
                                  {data?.isSampleSize ? (
                                    '0'
                                  ) : (
                                    <GradingCell
                                      value={eleItem?.gradings?.[index]?.grading}
                                      rowIndex={i}
                                      sizeIndex={index}
                                      disabled={!GradingAddEditAccess}
                                      onChange={(value, rowIndex, sizeIndex) => handleData(rowIndex, value, sizeIndex)}
                                    />
                                  )}
                                </th>
                              );
                            })}
                          </tr>
                        </table>
                      ) : (
                        <table className="inner-size-table">
                          <tr>
                            {defaultSizeRangeColum?.map((data, index) => {
                              return (
                                <th className="pl-0 text-center pt-0 pb-0 pr-16" key={data?.id}>
                                  {data?.isSampleSize ? (
                                    '0'
                                  ) : (
                                    <GradingCell
                                      value={eleItem?.gradings?.[index]?.grading}
                                      rowIndex={i}
                                      sizeIndex={index}
                                      disabled={!GradingAddEditAccess}
                                      onChange={(value, rowIndex, sizeIndex) => handleData(rowIndex, value, sizeIndex)}
                                    />
                                  )}
                                </th>
                              );
                            })}
                          </tr>
                        </table>
                      )}
                    </td>
                    <td>
                      <input
                        className="form-control text-bordered w-100 text-bold text-center"
                        type="number"
                        value={eleItem?.tolMinus}
                        onWheel={(e) => e.target.blur()}
                        onChange={(e) => handleToleranceData(i, e.target.value)}
                        disabled={!GradingAddEditAccess}
                      />
                    </td>
                    {ecommAcess?.[0]?.isActive && (
                      <td>
                        <Select
                          className="text-dark"
                          placeholder="UoM"
                          menuPosition="fixed"
                          name="sizeType"
                          classNamePrefix="common-select"
                          type="text"
                          styles={SystemMasterSelect}
                          isDisabled={!GradingAddEditAccess}
                          options={eCommSpecData}
                          getOptionLabel={(option) => `${option.name}`}
                          getOptionValue={(option) => `${option._id}`}
                          value={eleItem?.ecommSpec}
                          onChange={(selected) => handleEcommOptionSelect(i, selected)}
                        />
                      </td>
                    )}
                    {GradingDeleteAccess && (
                      <td className="text-right">
                        <CustomTooltip tooltipText={t('POMS.GRADING_GROUP.GRADING_TABLE.DELETE')}>
                          <button
                            aria-label={t('POMS.GRADING_GROUP.GRADING_TABLE.DELETE')}
                            type="button"
                            className="btn p-0"
                            onClick={() => {
                              handleSingleDelete(rowKey);
                            }}
                            disabled={!GradingDeleteAccess}
                          >
                            <i className="icon-Delete f-20" />
                          </button>
                        </CustomTooltip>
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={8}>
                  <p className="text-center m-0">{t('POMS.GRADING_GROUP.GRADING_TABLE.NO_POM_ENTRY')}</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {pendingDeleteId && (
        <ConfirmationPopup
          onOpen={!!pendingDeleteId}
          onConfirm={confirmSingleDelete}
          toggle={closeDeleteConfirm}
          title={t('POMS.GRADING_GROUP.DELETE_POM_CONFIRM.TITLE')}
          message={t('POMS.GRADING_GROUP.DELETE_POM_CONFIRM.MESSAGE')}
          secondMessage={t('POMS.GRADING_GROUP.DELETE_POM_CONFIRM.SECOND_MESSAGE')}
          loading={false}
        />
      )}
    </Container>
  );
}

export default GradingTable;
